import { Icon, type IconName } from './Icon';
import { HexisSymbol } from '@marcianus/design/components';

/**
 * EmptyState — estado vazio consistente (ícone + título + subtítulo + CTA).
 *
 * O `icon` é um `IconName`, e não um `ReactNode`. Não é uma restrição gratuita:
 * com `ReactNode`, alguém passou um emoji — e depois toda a gente copiou. A app
 * acumulou pictogramas espalhados por 14 ecrãs, contra a doutrina («sem emoji»),
 * porque a assinatura do componente PERMITIA.
 *
 * Tipado, o emoji deixa de compilar. A doutrina passa a ser imposta pelo
 * compilador, e não pela memória de quem escreve o próximo ecrã — que é a única
 * forma de uma regra sobreviver a uma equipa.
 */
/** `'hexis'` = o símbolo da marca. É a exceção CERTA: a doutrina manda que o
 *  estado vazio fale pelo Hexis («skeleton + copy do Hexis»), e o símbolo é a
 *  marca — não um pictograma decorativo. Tudo o resto é um ícone Lucide. */
export function EmptyState({ icon = 'info', title, subtitle, cta, onCta, compact = false }: {
  icon?: IconName | 'hexis';
  title?: string;
  subtitle?: string;
  cta?: string;
  onCta?: () => void;
  compact?: boolean;
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', gap: 10, padding: compact ? '24px 16px' : '44px 24px', color: 'var(--fg-40)',
    }}>
      <span style={{
        width: compact ? 40 : 52, height: compact ? 40 : 52, borderRadius: 'var(--r-lg)',
        display: 'grid', placeItems: 'center',
        background: 'var(--purple-10)', border: '1px solid var(--border-accent)', color: 'var(--purple-50)',
      }}>
        {icon === 'hexis'
          ? <HexisSymbol size={compact ? 22 : 28} />
          : <Icon name={icon} size={compact ? 20 : 24} />}
      </span>
      {title && <div style={{ fontWeight: 700, fontSize: compact ? 14 : 16, color: 'var(--fg-100)' }}>{title}</div>}
      {subtitle && <div style={{ fontSize: compact ? 12 : 13, maxWidth: 320, lineHeight: 1.45 }}>{subtitle}</div>}
      {cta && onCta && <button className="btn btn-gold btn-sm" style={{ marginTop: 6 }} onClick={onCta}>{cta}</button>}
    </div>
  );
}
